import type { Preferences, SessionUser } from '@cobliteam/common-types';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import GlobalSearch from '@cobliteam/global-search';
import { useLocalStorage } from '@cobliteam/hooks';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import InsightsButton from '@cobliteam/insights';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import AppUserButton from '@cobliteam/ui-business/components/AppUser';
import { useThrottleFn } from 'ahooks';
import { memo, useEffect, useState } from 'react';
import { SidebarTrigger } from '../AppSidebar';
import DialogTypeformSurvey from '../dialogs/DialogTypeformSurvey';
import ToggleFeatureFlag from '../ToggleFeatureFlag';
import AIButton from './components/AIButton';
import FeedbackButton from './components/FeedbackButton';
import FilterGroupComponent from './components/FilterGroupComponent';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import LogoComponent from './components/LogoComponent';
import NotificationsButton from './components/Notifications/NotificationsButton';

interface AppHeaderProps {
  user: SessionUser;
  logout: () => void;
}

const AppHeader = ({ user, logout }: AppHeaderProps) => {
  const { t } = useTranslation('app-header');
  const [isScrolled, setIsScrolled] = useState(false);
  const [localStorage, setLocalStorage] = useLocalStorage<Preferences>('preferences');
  const { runtimeFlags } = useDashboard();
  const featureFlags = useFeatureFlags();

  // Scroll handling
  const { run: handleScroll } = useThrottleFn(
    () => {
      const contentArea = document.getElementById('app-body');
      if (!contentArea) return;
      setIsScrolled(contentArea.scrollTop > 0);
    },
    { wait: 500 }
  );

  useEffect(() => {
    const contentArea = document.getElementById('app-body');
    if (!contentArea) return;

    contentArea.addEventListener('scroll', handleScroll);
    return () => contentArea.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const alreadyTryNewSidebar = (value: boolean | undefined) => {
    setLocalStorage({ ...localStorage, typeFormSidebarShowed: value });
  };

  return (
    <header
      data-testid='app-header'
      className={cn('sticky top-0 z-50 flex h-14 items-center justify-between gap-2 border-gray-6 bg-white px-2 pl-4 transition-shadow duration-200', {
        'border-b shadow-sm': isScrolled,
      })}
    >
      <div className='flex shrink-0 items-center justify-start gap-2'>
        <SidebarTrigger className='-ml-1 mr-2 desktop:hidden rounded-lg border border-gray-5' />
        <LogoComponent />
        <FilterGroupComponent />
        <DialogTypeformSurvey title={t('survey.title')} typeformId='Rux2GHPp' open={localStorage?.typeFormSidebarShowed === false} onClose={() => alreadyTryNewSidebar(true)} />
        <ToggleFeatureFlag
          flagKey='sidebar'
          onChangeValue={(value) => {
            if (value === false && localStorage?.typeFormSidebarShowed !== true) {
              alreadyTryNewSidebar(false);
            }
          }}
          labelEnabled={t('sidebar.backToOld')}
          labelEnabledTooltip={t('sidebar.backToOldTooltip')}
          labelDisabled={t('sidebar.testNew')}
          labelDisabledTooltip={t('sidebar.testNewTooltip')}
          iconEnabled={<Icon name='panel-left-close' />}
          iconDisabled={<Icon name='panel-left-open' />}
          className='min-w-[210px]'
        />
      </div>

      <div className='flex items-center justify-end gap-2.5'>
        {!runtimeFlags.noValidGroupOrVehicles && <GlobalSearch />}
        {featureFlags.aiChat && <AIButton />}
        <InsightsButton />
        <FeedbackButton />
        <NotificationsButton />
        <LanguageSwitcher />
        <AppUserButton user={user} logout={logout} />
      </div>
    </header>
  );
};

export default memo(AppHeader, (prevProps, nextProps) => {
  return prevProps.user.id === nextProps.user.id;
});
